import snapshot from "@/generated/plans.json";

/**
 * O catálogo de planos, lido do instantâneo gerado do banco.
 *
 * Nada aqui é digitado à mão: preço, limite, matriz de recursos e add-ons saem
 * de `src/generated/plans.json`, que `scripts/sync-plans.mjs` tira das tabelas
 * `plans`, `plan_features`, `plan_addons` e `plan_limits`. O gate `check:plans`
 * falha quando o instantâneo se afasta do banco.
 *
 * Por que não continuar com literais em TypeScript: o preço do mesmo plano
 * estava escrito em cinco lugares, e ninguém comparava os cinco. Dois catálogos
 * chegaram a discordar sobre o que "premium" significa — 5 imóveis num, 10 no
 * outro — e a RLS, que lê `plan_limits`, obedecia a um terceiro número.
 *
 * `null` é ILIMITADO, aqui como no banco. Quem quiser exibir ∞ converte na
 * borda; quem quiser comparar usa `cabeNoLimite()`, que trata o nulo.
 */

export type PlanKeyV3 = "free" | "essential" | "professional" | "premium" | "enterprise";

export interface RecursoDoPlano {
  incluido: boolean;
  detalhe: string | null;
}

export interface PlanoV3 {
  key: PlanKeyV3;
  nome: string;
  descricao: string;
  precoMensal: number | null;
  precoAnual: number | null;
  descontoAnualPct: number | null;
  /** null = ilimitado */
  maxImoveis: number | null;
  /** null = ilimitado */
  maxContratos: number | null;
  slaHoras: number | null;
  cta: string;
  contatoComercial: boolean;
  popular: boolean;
  /** Meses grátis antes de o preço entrar (só o Free tem). */
  freeMeses: number | null;
  /** O que o Free passa a custar depois dos meses grátis. */
  precoAposGratis: number | null;
  ordem: number;
  recursos: Record<string, RecursoDoPlano>;
}

export interface Recurso {
  key: string;
  label: string;
  ordem: number;
}

export interface AddonDoPlano {
  key: string;
  nome: string;
  preco: number;
  unidade: string;
  /** Vazio = vale para todos os planos. */
  planos: string[];
  stripePriceId: string | null;
  ordem: number;
}

export const PLANOS: PlanoV3[] = snapshot.planos as PlanoV3[];
export const RECURSOS: Recurso[] = snapshot.recursos as Recurso[];
export const ADDONS: AddonDoPlano[] = snapshot.addons as AddonDoPlano[];

/** price_id do Stripe → o que aquele preço entrega. Inclui os preços LEGADOS. */
export const PRECOS_LEGADOS: Record<string, { plano: string; versao: string | null; maxImoveis: number | null; maxContratos: number | null }> =
  snapshot.legado as Record<string, { plano: string; versao: string | null; maxImoveis: number | null; maxContratos: number | null }>;

export const ORDEM_DOS_PLANOS: PlanKeyV3[] = PLANOS.map((p) => p.key);

export function plano(key: string): PlanoV3 | null {
  return PLANOS.find((p) => p.key === key) ?? null;
}

/**
 * Posição do plano na escada. Serve para "este recurso exige pelo menos X".
 * Plano desconhecido — legado, cortesia com nome antigo — vale -1 e não passa
 * em portão nenhum: falhar fechado é o comportamento certo aqui.
 */
export function degrau(key: string): number {
  return ORDEM_DOS_PLANOS.indexOf(key as PlanKeyV3);
}

export function peloMenos(planoAtual: string, minimo: PlanKeyV3): boolean {
  const a = degrau(planoAtual);
  const b = degrau(minimo);
  return a >= 0 && b >= 0 && a >= b;
}

/** O plano inclui o recurso? Recurso desconhecido responde `false`. */
export function temRecurso(planoAtual: string, featureKey: string): boolean {
  return plano(planoAtual)?.recursos?.[featureKey]?.incluido ?? false;
}

export function detalheDoRecurso(planoAtual: string, featureKey: string): string | null {
  return plano(planoAtual)?.recursos?.[featureKey]?.detalhe ?? null;
}

/** Menor plano que inclui o recurso — é o que a tela oferece ao bloquear. */
export function planoMinimoCom(featureKey: string): PlanoV3 | null {
  return PLANOS.find((p) => p.recursos?.[featureKey]?.incluido) ?? null;
}

/** `null` no limite é ilimitado; `quantos < limite` com nulo daria falso. */
export function cabeNoLimite(quantos: number, limite: number | null): boolean {
  return limite === null || quantos < limite;
}

export function limiteLegivel(limite: number | null): string {
  return limite === null ? "Ilimitado" : String(limite);
}

/**
 * O limite com a unidade concordando em número: "1 imóvel", "3 imóveis", "Imóveis ilimitados".
 * `limiteLegivel` sozinho dava "1 imóveis" e "Ilimitado imóveis" no cartão do plano.
 */
export function limiteComUnidade(limite: number | null, singular: string, plural: string): string {
  if (limite === null) return `${plural.charAt(0).toUpperCase()}${plural.slice(1)} ilimitados`;
  return `${limite} ${limite === 1 ? singular : plural}`;
}

export function precoLegivel(valor: number | null): string {
  if (valor === null) return "Sob consulta";
  if (valor === 0) return "Grátis";
  return valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/** Quanto o plano anual economiza no ano, em reais. */
export function economiaAnual(p: PlanoV3): number | null {
  if (p.precoMensal === null || p.precoAnual === null || p.precoMensal === 0) return null;
  return Number((p.precoMensal * 12 - p.precoAnual).toFixed(2));
}

/**
 * O preço do Stripe está configurado, ou ainda é espaço reservado?
 *
 * Os `stripe_price_id` nascem como `STRIPE_PRICE_ID_...` e o checkout precisa
 * recusá-los — oferecer compra que falha é pior que não oferecer.
 */
export function precoConfigurado(priceId: string | null | undefined): boolean {
  return !!priceId && !priceId.startsWith("STRIPE_PRICE_ID");
}

/**
 * O plano pode ser assinado AGORA, neste ambiente?
 *
 * Os IDs vêm do banco, não do instantâneo: o gfi-dev tem preço de teste e a
 * produção, de produção. Passar o par de fora é o que impede a tela de oferecer
 * o preço do ambiente errado.
 */
export function planoVendavel(
  p: PlanoV3,
  precos?: { mensal: string | null; anual: string | null },
): boolean {
  if (p.contatoComercial) return false;
  if (p.precoMensal === 0) return true;
  return precoConfigurado(precos?.mensal) || precoConfigurado(precos?.anual);
}

/** Add-ons oferecidos a um plano — lista vazia em `planos` vale para todos. */
export function addonsDoPlano(planoAtual: string): AddonDoPlano[] {
  return ADDONS.filter((a) => a.planos.length === 0 || a.planos.includes(planoAtual));
}

/**
 * Onde o Free deixa de ser grátis.
 *
 * A planilha diz "gratuito por 3 meses e após este prazo o valor vai para
 * R$ 9,90". A conta sai da data de criação da conta — a cobrança em si depende
 * de assinatura no Stripe, que ainda não existe; o que esta função sustenta é a
 * TELA dizer a verdade desde o primeiro dia.
 */
export function fimDoPeriodoGratis(criadoEm: string | Date | null | undefined): Date | null {
  const free = plano("free");
  if (!free?.freeMeses || !criadoEm) return null;
  const inicio = new Date(criadoEm);
  if (Number.isNaN(inicio.getTime())) return null;
  const fim = new Date(inicio);
  fim.setMonth(fim.getMonth() + free.freeMeses);
  return fim;
}

export function diasAteFimDoGratis(criadoEm: string | Date | null | undefined, agora = new Date()): number | null {
  const fim = fimDoPeriodoGratis(criadoEm);
  if (!fim) return null;
  return Math.ceil((fim.getTime() - agora.getTime()) / 86_400_000);
}
